'use client'

import { ArrowRightIcon, InfoIcon, LockKeyIcon, XIcon } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import Loading from '@/core/components/globals/loading';
import { useMutation } from '@tanstack/react-query';
import DvModal from '@/core/components/modal/dv-modal';
import CreateDcaModal from '@/core/components/modal/dca/create-dca';
import { useDcaStrategyStore } from '@/store/dca-strategy/store';
import { Tooltip } from 'react-tooltip';
import { get } from 'lodash';
import { DcaStatus } from '@/store/dca-strategy/enum';
import { DcaService } from '@/core/services/dcaService';
import { CoinModelRequest, DcaStrategy } from '@/types/dca';
import { lazy, Suspense } from 'react';
const CryptoIconNoSSR = lazy(() => import('../../icons/web3-icon'));

import { TokenIcon } from '@web3icons/react';
import { useTranslation } from 'react-i18next';

const StartDcaModal = ({onClose}: {onClose: () => void}) => {
  const { t } = useTranslation();
  const setDcaStrategyCreate = useDcaStrategyStore((state) => state.setDcaStrategyCreate);
  const [openCreateDcaModal, setOpenCreateDcaModal] = useState<boolean>(false);
  const [dcaStrategyList, setDcaStrategyList] = useState<DcaStrategy[]>([]);
  const { mutate: getAllDCAModel, isLoading: isLoadinggetAllDCAModel } = useMutation({
    mutationFn: () => DcaService.getAllDCAModel(),
    onSuccess: (data: DcaStrategy[]) => {
      // Parse coins and percentages strings to create coinModelRequest
      const normalizePercentToken = (token?: string): number => {
        if (!token) return 0;
        let s = String(token).trim();
        // remove percent sign, replace comma decimal
        s = s.replace(/%/g, '').replace(',', '.');
        const n = parseFloat(s);
        if (!Number.isFinite(n)) return 0;
        // If token is a fraction (0..1), convert to percent. Else assume already percent.
        const percent = n <= 1 ? n * 100 : n;
        // Keep within 0..100 and round to 2 decimals for UI display
        const clamped = Math.min(100, Math.max(0, percent));
        return Math.round(clamped * 100) / 100;
      };

      const transformedData = data.map((item) => {
        const coinsArray = item.coins?.split('/') || [];
        const percentagesArray = item.percentages?.split('/') || [];

        const coinModelRequest: CoinModelRequest[] = coinsArray.map((coin, index) => ({
          name: coin.trim(),
          percentage: normalizePercentToken(percentagesArray[index])
        }));

        return {
          ...item,
          dcaBalance: 0,
          coinModelRequest
        };
      });

      setDcaStrategyList(transformedData);
    }
  });

  const { mutate: createDCAStrategy, isLoading: isCreatingDca } = useMutation({
    mutationFn: async (payload: DcaStrategy) => {
      const data = await DcaService.createDCAStrategy(payload);
      return {
        ...data,
        // API returns basis points (0..10000). Convert back to UI percent (0..100) with 2 decimals
        coinModelRequest: get(data, 'dcaStrategyAllocations', []).map((wallet) => ({
          name: wallet.coinCode,
          percentage: Math.round(((wallet.percentage ?? 0) / 100) * 100) / 100,
        })),
        isAddCoins: !!data?.dcaStrategyAllocations,
        minimumInvestmentAmount: 0,
        dcaModelId: payload.id
      };
    },
    onSuccess: (data) => {
      setDcaStrategyCreate(data);
      setOpenCreateDcaModal(true);
    },
  });

  const handleTryIt = (dcaStrategy: DcaStrategy) => {
    if (isCreatingDca) return;
    createDCAStrategy({ ...dcaStrategy, dcaStatus: DcaStatus.EDITING, dcaModelId: dcaStrategy.id });
  }

  const handleCreateSuccess = () => {
    onClose();
  }

  useEffect(() => {
    getAllDCAModel();
  }, []);

  return (
    <>
      {isCreatingDca && <div className="screen-loading"><Loading /></div>}
      <div className="general-modal dca-start-modal">
        <div className="general-modal__header">
          {t('dca.choose_a_dca_model')}
          <div className="general-modal__close" onClick={onClose}>
            <XIcon />
          </div>
        </div>
        <div className="general-modal__content">
          {
            isLoadinggetAllDCAModel ? (
              <Loading />
            ) : (
              (() => {
                // Identify Custom strategy by description field
                const customStrategy = dcaStrategyList.find(
                  (item) => item.description?.toLowerCase() === 'custom'
                );
                const presetStrategies = dcaStrategyList.filter(
                  (item) => item.description?.toLowerCase() !== 'custom'
                );

                return (
                  <div className="dca__list-modal">
                    {presetStrategies.map((item: DcaStrategy, index: number) => (
                      <div key={item.id} className={`modal-card`} onClick={() => handleTryIt(item)}>
                        <div>
                          <div className="modal-card__header">
                            <div className="d-flex align-items-center gap-2">
                              <LockKeyIcon size={20} weight="fill" />
                              <div className="modal-card__header-title">{item.description}</div>
                            </div>
                            {
                              get(item, 'coinModelRequest', []).some((coin: CoinModelRequest) => coin.percentage > 0) && (
                                <div>
                                  <div data-tooltip-id={`dca-tooltip-${index}`} className="d-flex cursor-pointer">
                                    <InfoIcon size={18} weight="fill" color="var(--text-secondary)" style={{ color: 'var(--text-secondary)' }} />
                                  </div>
                                  <Tooltip
                                    id={`dca-tooltip-${index}`}
                                    render={() => (
                                      <div className="d-flex gap-2">
                                        <div>
                                          {get(item, 'coinModelRequest', []).map((coin: CoinModelRequest) => (
                                            <div key={coin.name}>
                                              {coin.name}:
                                            </div>
                                          ))}
                                        </div>
                                         <div>
                                           {get(item, 'coinModelRequest', []).map((coin: CoinModelRequest) => (
                                             <div key={coin.name}>
                                               {typeof coin.percentage === 'number' ? coin.percentage.toFixed(2) : coin.percentage}%
                                             </div>
                                           ))}
                                         </div>
                                      </div>
                                    )}
                                    place="bottom"
                                  />
                                </div>
                              )
                            }
                          </div>
                          <div className="modal-card__description-coins">
                            {
                              get(item, 'coinModelRequest', []).map((coin: CoinModelRequest) => (
                                process.env.NODE_ENV === "development" ? (
                                  <Suspense key={coin.name} fallback={<div style={{ width: 32, height: 32 }} />}>
                                    <CryptoIconNoSSR token={coin.name} />
                                  </Suspense>
                                ) : (
                                  <TokenIcon key={coin.name} size={32} symbol={coin.name} variant={['GALA', 'FET'].includes(coin.name) ? 'mono' : 'branded'} />
                                )
                              ))
                            }
                          </div>
                        </div>
                        <div className="modal-card__action">
                          <a>
                            {t('dca.try_it')}
                          </a>
                          <ArrowRightIcon size={20} />
                        </div>
                      </div>
                    ))}
                    {/* Custom DCA Creation Card */}
                    {customStrategy && (
                      <div className={`modal-card modal-card--custom`} onClick={() => handleTryIt(customStrategy)}>
                        <div>
                          <div className="modal-card__header">
                            <div className="d-flex align-items-center gap-2">
                              <LockKeyIcon size={20} weight="fill" />
                              <div className="modal-card__header-title">{customStrategy.description}</div>
                            </div>
                          </div>
                          <div className="modal-card__description-coins">
                            <div style={{ fontSize: '14px', opacity: 0.8 }}>{t('dca.build_your_own_portfolio')}</div>
                          </div>
                        </div>
                        <div className="modal-card__action">
                          <a>
                            {t('dca.try_it')}
                          </a>
                          <ArrowRightIcon size={20} />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()
            )
          }
        </div>
        <div className="general-modal__footer general-modal__footer--right">
          <button className="cta-button cta-medium cta-white btn-min-width-140" onClick={onClose}>
            <span>{t('common.close')}</span>
          </button>
          <button 
            className="cta-button cta-medium cta-blue" 
            onClick={() => {
              const customStrategy = dcaStrategyList.find(
                (item) => item.description?.toLowerCase() === 'custom'
              );
              if (customStrategy) handleTryIt(customStrategy);
            }}
            disabled={!dcaStrategyList.some((item) => item.description?.toLowerCase() === 'custom')}
          >
            <span>{t('common.configure')} DCA</span>
          </button>
        </div>
      </div>
      <DvModal open={openCreateDcaModal} onClose={() => setOpenCreateDcaModal(false)}>
        <CreateDcaModal onClose={() => setOpenCreateDcaModal(false)} onSuccess={handleCreateSuccess} />
      </DvModal>
    </>
  )
}

export default StartDcaModal
